import { CloseButton, Menu, Table } from '@mantine/core';
import {
	IconChevronDown,
	IconChevronUp,
	IconEdit,
	IconShoppingCartPlus,
	IconTrash,
} from '@tabler/icons-react';
import { useState } from 'react';
import { useParams } from 'react-router-dom';
import { DeleteModal } from '../../../components/DeleteModal';
import { TableRowCollapsible } from '../../../components/TableRowCollapsible';
import { TableRowMenu } from '../../../components/TableRowMenu';
import { useModal } from '../../../hooks/useModal';
import { defaultIconStyle } from '../../../theme/styling/iconStyling';
import { mutate } from '../../../util/api/mutate';
import { query } from '../../../util/api/query';
import {
	formatToDateString,
	formatToTimeString,
} from '../../../util/formatting';
import { mapNewDate } from '../../../util/mapNewDate';
import { undefinedCheck } from '../../../util/undefinedCheck';
import { useFormatDiscount } from '../hooks/useFormatDiscount';
import type { PriceChangeProductDto } from '../types/PriceChangeProductDto';
import type { PromotionDto, PromotionProductDto } from '../types/PromotionDto';
import { EditPromotionModal } from './EditPromotionModal';
import { PromotionRowProducts } from './PromotionRowProducts';
import { SelectProductsModal } from './SelectProductsModal';
import { KeyedRow } from '../../../components/KeyedRow';

interface PromotionRowProps {
	originalPromotion: PromotionDto;
	triggerListUpdate: () => void;
}

export function PromotionRow({
	originalPromotion,
	triggerListUpdate,
}: PromotionRowProps) {
	const { organisation } = useParams();
	const [promotion, setPromotion] = useState(originalPromotion);

	const [productsOpen, setProductsOpen] = useState(false);
	const discount = useFormatDiscount(promotion);

	const productsQuery = query<PromotionProductDto[]>(
		`${organisation}/promotions/products-by-promotion?promotionId=${promotion.id}&branchId=${promotion.branchId}`,
		productsOpen,
	);

	const editModal = useModal();
	const deleteModal = useModal();
	const productsModal = useModal();

	const deleteProductMutation = mutate<PriceChangeProductDto>(
		`${organisation}/promotions/delete/${promotion.id}`,
	);

	return (
		<>
			<SelectProductsModal
				close={productsModal.close}
				isOpened={productsModal.isOpen}
				triggerListUpdate={() => {
					productsQuery.remove();
					productsQuery.refetch();
				}}
				promotionId={promotion.id}
				branchId={promotion.branchId}
			/>
			<EditPromotionModal
				close={editModal.close}
				isOpened={editModal.isOpen}
				updateTableValue={(p) => setPromotion(p)}
				promotion={{
					branchId: originalPromotion.branchId,
					till: originalPromotion.tillDesc,
					id: Number.parseInt(originalPromotion.id),
					days: originalPromotion.days,
					typeId: originalPromotion.priceTypeId,
					discount: originalPromotion.discount.toString(),
					description: originalPromotion.description,
					qty: originalPromotion.quantity.toString(),
					endDate: mapNewDate(originalPromotion.endDate),
					startDate: mapNewDate(originalPromotion.startDate),
				}}
			/>
			<DeleteModal
				isOpened={deleteModal.isOpen}
				close={deleteModal.close}
				titleType={'Promotion'}
				itemDescription={''}
				mutation={deleteProductMutation}
				onComplete={triggerListUpdate}
			/>
			<KeyedRow>
				<Table.Td>{undefinedCheck(promotion.branch)}</Table.Td>
				<Table.Td>{undefinedCheck(promotion.tillDesc)}</Table.Td>
				<Table.Td>{undefinedCheck(promotion.description)}</Table.Td>
				<Table.Td>{promotion.quantity}</Table.Td>
				<Table.Td>{discount}</Table.Td>
				<Table.Td>{undefinedCheck(promotion.priceType)}</Table.Td>
				<Table.Td>{formatToDateString(promotion.startDate)}</Table.Td>
				<Table.Td>{formatToTimeString(promotion.startDate)}</Table.Td>
				<Table.Td>{formatToDateString(promotion.endDate)}</Table.Td>
				<Table.Td>{formatToTimeString(promotion.endDate)}</Table.Td>
				<Table.Td>
					<CloseButton
						icon={
							!productsOpen ? (
								<IconChevronDown {...defaultIconStyle} />
							) : (
								<IconChevronUp {...defaultIconStyle} />
							)
						}
						onClick={() => setProductsOpen(!productsOpen)}
					/>
				</Table.Td>
				<Table.Td>
					<TableRowMenu>
						<Menu.Item
							leftSection={<IconEdit {...defaultIconStyle} />}
							onClick={() => editModal.open()}
						>
							Edit Promotion
						</Menu.Item>
						<Menu.Item
							leftSection={<IconShoppingCartPlus {...defaultIconStyle} />}
							onClick={() => productsModal.open()}
						>
							Add Products
						</Menu.Item>
						<Menu.Item
							leftSection={<IconTrash {...defaultIconStyle} />}
							c={'red'}
							onClick={() => deleteModal.open()}
						>
							Delete
						</Menu.Item>
					</TableRowMenu>
				</Table.Td>
			</KeyedRow>
			<TableRowCollapsible isOpen={productsOpen} columnRows={13}>
				<PromotionRowProducts productsQuery={productsQuery} />
			</TableRowCollapsible>
		</>
	);
}
